import React, {
    Suspense,
    createContext,
    lazy,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

const ChatDrawer = lazy(() => import('../components/chat-drawer/chat-drawer'));

/**
 * ChatDrawerContext
 * -----------------
 * App-wide controller for the inline lead chat experience. Renders a single
 * ChatDrawer instance and exposes `openChat()` so lead summary rows/cards can
 * pop open a conversation without navigating to a detail page.
 *
 * Usage from a row/card:
 *   const { openChat } = useChatDrawer();
 *   openChat({ groupId, title: lead.name, subtitle: lead.propertyName });
 */
const ChatDrawerContext = createContext({
    openChat: () => {},
    closeChat: () => {},
    isOpen: false,
});

export const useChatDrawer = () => useContext(ChatDrawerContext);

const EMPTY_STATE = {
    groupId: null,
    title: '',
    subtitle: '',
    replyContext: null,
    onMessageSent: null,
    onMessagesRead: null,
};

class ChatDrawerErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Chat drawer crashed:', error, errorInfo);
        this.props.onRecover?.();
    }

    render() {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}

export const ChatDrawerProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [config, setConfig] = useState(EMPTY_STATE);
    const [drawerKey, setDrawerKey] = useState(0);

    const openChat = useCallback((next = {}) => {
        if (!next.groupId) {
            console.warn('openChat called without a groupId; ignoring.');
            return;
        }
        setConfig({ ...EMPTY_STATE, ...next });
        setOpen(true);
    }, []);

    const closeChat = useCallback(() => {
        setOpen(false);
    }, []);

    const handleOpenChange = useCallback((nextOpen) => {
        setOpen(nextOpen);
    }, []);

    const handleDrawerRecover = useCallback(() => {
        setOpen(false);
        setConfig(EMPTY_STATE);
        setDrawerKey((key) => key + 1);
    }, []);

    const value = useMemo(
        () => ({ openChat, closeChat, isOpen: open }),
        [openChat, closeChat, open]
    );

    return (
        <ChatDrawerContext.Provider value={value}>
            {children}
            {(open || config.groupId) && (
                <ChatDrawerErrorBoundary key={drawerKey} onRecover={handleDrawerRecover}>
                    <Suspense fallback={null}>
                        <ChatDrawer
                            open={open}
                            onOpenChange={handleOpenChange}
                            groupId={config.groupId}
                            title={config.title}
                            subtitle={config.subtitle}
                            replyContext={config.replyContext}
                            onMessageSent={config.onMessageSent}
                            onMessagesRead={config.onMessagesRead}
                        />
                    </Suspense>
                </ChatDrawerErrorBoundary>
            )}
        </ChatDrawerContext.Provider>
    );
};

export default ChatDrawerProvider;
